import { apiClient as api } from './api';
import type { CoverageRule } from '@shared/control-period';
import {
  CategoryValidityMode,
  ControlType,
  Issuer,
  PaginatedResponse,
  AdminUser,
  AdminUserDetails,
  CreateUserPayload,
  UpdateUserPayload,
  AuditLogEntry,
  DashboardStats,
  SystemHealth,
  CreateControlTypeRequest,
  UpdateControlTypeRequest,
  CloneControlTypeRequest,
  CreateIssuerRequest,
  UpdateIssuerRequest,
  DocumentRequirement
} from '@/types';
import type { FilterRule } from '@/types/farm-profile';

/**
 * Lightweight affärspartner record returned by the admin allowlist endpoints.
 * Mirrors the backend `AllowedAffarspartnerDto`.
 */
export interface AffarspartnerSummary {
  id: string;
  orgnr: string;
  name: string;
}

export interface ControlTypeFilters {
  includeInactive?: boolean;
  issuerId?: string;
  page?: number;
  limit?: number;
}

export interface UserFilters {
  page?: number;
  limit?: number;
  role?: string;
  search?: string;
}

export interface AuditLogFilters {
  page?: number;
  limit?: number;
  action?: string;
  userId?: string;
  startDate?: Date;
  endDate?: Date;
}

// Backward compatibility aliases
export type VersionedControlTypeFilters = ControlTypeFilters;
export type CreateVersionedControlTypeRequest = CreateControlTypeRequest;
export type UpdateVersionedControlTypeRequest = UpdateControlTypeRequest;
export type CloneVersionedControlTypeRequest = CloneControlTypeRequest;

export class AdminService {
  /**
   * DASHBOARD & STATISTICS
   */
  static async getDashboardStats(): Promise<DashboardStats> {
    const response = await api.get<{ data: DashboardStats }>('/admin/dashboard');
    return response.data;
  }

  static async getSystemHealth(): Promise<SystemHealth> {
    const response = await api.get<{ data: SystemHealth }>('/admin/system-health');
    return response.data;
  }

  /**
   * CONTROL TYPES MANAGEMENT (UNIFIED WITH ISSUER)
   */
  static async getControlTypes(filters: ControlTypeFilters = {}): Promise<PaginatedResponse<ControlType>> {
    console.log('🔍 AdminService.getControlTypes: Called with filters:', filters);

    const params: Record<string, string> = {};

    if (filters.includeInactive) params['includeInactive'] = 'true';
    if (filters.issuerId) params['issuerId'] = filters.issuerId;
    if (filters.page) params['page'] = filters.page.toString();
    if (filters.limit) params['limit'] = filters.limit.toString();

    console.log('🔍 AdminService: Making request with params:', params);

    try {
      const responseData = await api.get<any>('/admin/control-types', params);

      console.log('🔍 AdminService: Raw responseData:', responseData);
      console.log('🔍 AdminService: responseData.data:', responseData.data);

      // Backend returns: { success: true, data: { controlTypes: [], total, page, limit, totalPages } }
      // We need to transform to: { items: [], pagination: { ... } }
      const data = responseData.data || responseData;
      const controlTypes = data.controlTypes || data.items || [];

      const result = {
        items: controlTypes,
        pagination: {
          page: data.page || filters.page || 1,
          limit: data.limit || filters.limit || 10,
          total: data.total || 0,
          totalPages: data.totalPages || 0,
          hasNext: (data.page || 1) < (data.totalPages || 0),
          hasPrev: (data.page || 1) > 1
        }
      };

      console.log('🔍 AdminService: Returning result:', result);

      return result;
    } catch (error) {
      console.error('🔍 AdminService: Error fetching control types:', error);
      throw error;
    }
  }

  static async getControlType(id: string): Promise<ControlType> {
    const response = await api.get<{ data: ControlType }>(`/admin/control-types/${id}`);
    return response.data;
  }

  static async createControlType(data: CreateControlTypeRequest): Promise<ControlType> {
    const response = await api.post<{ data: ControlType }>('/admin/control-types', data);
    return response.data;
  }

  static async updateControlType(id: string, data: UpdateControlTypeRequest): Promise<ControlType> {
    const response = await api.put<{ data: ControlType }>(`/admin/control-types/${id}`, data);
    return response.data;
  }

  static async deleteControlType(id: string): Promise<void> {
    await api.delete(`/admin/control-types/${id}`);
  }

  static async cloneControlType(id: string, data: CloneControlTypeRequest): Promise<ControlType> {
    const response = await api.post<{ data: ControlType }>(`/admin/control-types/${id}/clone`, data);
    return response.data;
  }

  /**
   * Affärspartner allowlist for kontrolltyper. An empty list means
   * "open to everyone" — only restricted kontrolltyper carry entries.
   *
   * The api client already unwraps the backend's `{ success, data }`
   * envelope (see TransformInterceptor), so these calls return the
   * controller's direct return value.
   *
   * No bulk-list endpoint exists: the master table holds ~4000 rows and
   * the admin UI builds the allowlist by typing an orgnr or id and
   * looking up one row at a time.
   */
  static async lookupAffarspartner(query: string): Promise<AffarspartnerSummary> {
    return api.get<AffarspartnerSummary>('/admin/affarspartners/lookup', { q: query });
  }

  static async getAllowedAffarspartners(controlTypeId: string): Promise<AffarspartnerSummary[]> {
    return api.get<AffarspartnerSummary[]>(
      `/admin/control-types/${controlTypeId}/allowed-affarspartners`,
    );
  }

  static async setAllowedAffarspartners(
    controlTypeId: string,
    affarspartnerIds: string[],
  ): Promise<AffarspartnerSummary[]> {
    return api.put<AffarspartnerSummary[]>(
      `/admin/control-types/${controlTypeId}/allowed-affarspartners`,
      { affarspartnerIds },
    );
  }

  // Backward compatibility aliases
  static async getVersionedControlTypes(filters: VersionedControlTypeFilters = {}) {
    return this.getControlTypes(filters);
  }

  static async getVersionedControlType(id: string) {
    return this.getControlType(id);
  }

  static async createVersionedControlType(data: CreateVersionedControlTypeRequest) {
    return this.createControlType(data);
  }

  static async updateVersionedControlType(id: string, data: UpdateVersionedControlTypeRequest) {
    return this.updateControlType(id, data);
  }

  static async deleteVersionedControlType(id: string) {
    return this.deleteControlType(id);
  }

  static async cloneVersionedControlType(id: string, data: CloneVersionedControlTypeRequest) {
    return this.cloneControlType(id, data);
  }

  /**
   * USER MANAGEMENT
   */
  static async getUsers(filters: UserFilters = {}): Promise<PaginatedResponse<AdminUser>> {
    const params = new URLSearchParams();

    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.role) params.append('role', filters.role);
    if (filters.search) params.append('search', filters.search);

    const queryString = params.toString();
    const url = `/admin/users${queryString ? `?${queryString}` : ''}`;

    const response = await api.get<{ users: AdminUser[], total: number, page: number, limit: number, totalPages: number }>(url);

    return {
      items: response.users,
      pagination: {
        page: response.page,
        limit: response.limit,
        total: response.total,
        totalPages: response.totalPages,
        hasNext: response.page < response.totalPages,
        hasPrev: response.page > 1
      }
    };
  }

  static async updateUserRole(userId: string, role: string): Promise<AdminUser> {
    const response = await api.put<AdminUser>(`/admin/users/${userId}/role`, { role });
    return response;
  }

  static async updateUserStatus(userId: string, isActive: boolean): Promise<AdminUser> {
    const response = await api.put<AdminUser>(`/admin/users/${userId}/status`, { isActive });
    return response;
  }

  static async createUser(data: CreateUserPayload): Promise<AdminUserDetails> {
    const response = await api.post<AdminUserDetails>('/admin/users', data);
    return response;
  }

  static async getUserDetails(userId: string): Promise<AdminUserDetails> {
    const response = await api.get<AdminUserDetails>(`/admin/users/${userId}`);
    return response;
  }

  static async updateUser(userId: string, data: UpdateUserPayload): Promise<AdminUserDetails> {
    const response = await api.put<AdminUserDetails>(`/admin/users/${userId}`, data);
    return response;
  }

  static async resetPassword(userId: string, password: string): Promise<void> {
    await api.put(`/admin/users/${userId}/password`, { password });
  }

  /**
   * AUDIT LOGS
   */
  static async getAuditLogs(filters: AuditLogFilters = {}): Promise<PaginatedResponse<AuditLogEntry>> {
    const params = new URLSearchParams();

    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.action) params.append('action', filters.action);
    if (filters.userId) params.append('userId', filters.userId);
    if (filters.startDate) params.append('startDate', filters.startDate.toISOString());
    if (filters.endDate) params.append('endDate', filters.endDate.toISOString());

    const queryString = params.toString();
    const url = `/admin/audit-logs${queryString ? `?${queryString}` : ''}`;

    const response = await api.get<{ data: AuditLogEntry[], pagination: any }>(url);

    return {
      items: response.data,
      pagination: response.pagination
    };
  }

  /**
   * ISSUER MANAGEMENT
   */
  static async getIssuers(includeInactive = false): Promise<{ issuers: Issuer[]; total: number; page: number; limit: number; totalPages: number }> {
    const params = new URLSearchParams();
    if (includeInactive) params.append('includeInactive', 'true');

    const queryString = params.toString();
    const url = `/admin/issuers${queryString ? `?${queryString}` : ''}`;

    const response = await api.get<{ issuers: Issuer[]; total: number; page: number; limit: number; totalPages: number }>(url);
    return response;
  }

  static async getIssuer(id: string): Promise<Issuer> {
    const response = await api.get<{ data: Issuer }>(`/admin/issuers/${id}`);
    return response.data;
  }

  static async createIssuer(data: CreateIssuerRequest): Promise<Issuer> {
    const response = await api.post<{ data: Issuer }>('/admin/issuers', data);
    return response.data;
  }

  static async updateIssuer(id: string, data: UpdateIssuerRequest): Promise<Issuer> {
    const response = await api.put<{ data: Issuer }>(`/admin/issuers/${id}`, data);
    return response.data;
  }

  static async deleteIssuer(id: string): Promise<void> {
    await api.delete(`/admin/issuers/${id}`);
  }

  static async deactivateIssuer(id: string): Promise<Issuer> {
    const response = await api.put<{ data: Issuer }>(`/admin/issuers/${id}/deactivate`, {});
    return response.data;
  }

  static async activateIssuer(id: string): Promise<Issuer> {
    const response = await api.put<{ data: Issuer }>(`/admin/issuers/${id}/activate`, {});
    return response.data;
  }

  /**
   * DOCUMENT REQUIREMENTS MANAGEMENT
   */
  static async createDocumentRequirement(
    controlTypeId: string,
    data: {
      tags: string[];
      /** Validity pre-selected on upload; null = none. */
      validityMode?: CategoryValidityMode | null;
      description: string;
      informationText?: string;
      isRequired?: boolean;
      /** Months a document stays valid; 'expires' mode only. */
      validityPeriod?: number | null;
      /** Annual mode: years relative to the control period's main year. */
      yearOffsets?: number[];
      coverageRule?: CoverageRule;
      sectionName?: string;
      displayOrder?: number;
      filterRule?: FilterRule | null;
    }
  ): Promise<DocumentRequirement> {
    const response = await api.post<any>(
      `/admin/control-types/${controlTypeId}/requirements`,
      data
    );
    return response;
  }

  static async updateDocumentRequirement(
    controlTypeId: string,
    requirementId: string,
    data: {
      tags?: string[];
      /** null clears the pre-selection; omit to leave it unchanged. */
      validityMode?: CategoryValidityMode | null;
      description?: string;
      informationText?: string;
      isRequired?: boolean;
      /** null clears the limit; omit to leave it unchanged. 'expires' mode only. */
      validityPeriod?: number | null;
      /** Annual mode: years relative to the control period's main year; omit to leave unchanged. */
      yearOffsets?: number[];
      coverageRule?: CoverageRule;
      sectionName?: string;
      displayOrder?: number;
      filterRule?: FilterRule | null;
    }
  ): Promise<DocumentRequirement> {
    const response = await api.put<any>(
      `/admin/control-types/${controlTypeId}/requirements/${requirementId}`,
      data
    );
    return response;
  }

  static async deleteDocumentRequirement(
    controlTypeId: string,
    requirementId: string
  ): Promise<void> {
    await api.delete(`/admin/control-types/${controlTypeId}/requirements/${requirementId}`);
  }

  /**
   * Bulk-assign displayOrder to a control type's requirements.
   *
   * Used by the Sortering tab, where requirements and form fields share one order
   * sequence per section. Form-field order rides along in the formConfiguration blob
   * saved separately; only requirements have a DB column of their own.
   *
   * @param controlTypeId - The control type the requirements belong to
   * @param updates - New displayOrder per requirement id
   */
  static async updateRequirementSortOrders(
    controlTypeId: string,
    updates: Array<{ id: string; displayOrder: number }>
  ): Promise<void> {
    if (updates.length === 0) return;
    await api.put(
      `/admin/control-types/${controlTypeId}/requirements/sort-orders`,
      { updates }
    );
  }
}